import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { apiFetch } from "../utils/api";
import { useAuth } from '../Services/AuthContext';
import { formatZARCurrency } from '../utils/formatUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Target, Package, Award, Repeat, Calendar as CalendarIcon, Eye } from 'lucide-react';
import { Badge } from './ui/badge';
import { Label } from './ui/label';

export function SalesMetrics({setViewOpportunity, setIsViewOpportunityOpen}) {

  const {
    idToken,
    currentPartner,
  } = useAuth();

  const currentDate = new Date();
  const currentMonthName = currentDate.toLocaleDateString('en-US', { month: 'short' });
  const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
  const nextMonthName = nextMonth.toLocaleDateString('en-US', { month: 'short' });
  const monthAfterNext = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 1);
  const monthAfterNextName = monthAfterNext.toLocaleDateString('en-US', { month: 'short' });
  const threeMonthRange = `${currentMonthName} - ${monthAfterNextName}`;
  const [metricsData, setMetricsData] = useState({});
  const [topTenCurrent, setTopTenCurrent] = useState(0);
  const [topTenNext, setTopTenNext] = useState(0);
  const [topTenAfterNext, setTopTenAfterNext] = useState(0);
  const [topTenCurrentRenewal, setTopTenCurrentRenewal] = useState(0);
  const [topTenNextRenewal, setTopTenNextRenewal] = useState(0);
  const [topTenAfterNextRenewal, setTopTenAfterNextRenewal] = useState(0);
  const [metricOpportunities, setMetricOpportunities] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('');
  const [isMetricDialogOpen, setIsMetricDialogOpen] = useState(false);


  const fetchMetrics = async () => {
    let tk = await idToken();
    let url = `/api/metrics/top_ten?partner_id=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`;
    const res = apiFetch(tk, url);
    res.then(async (response) => {
      setMetricsData(response.data)
      setTopTenCurrent(response.data.top_ten_new_seats_current.reduce((sum, item) => {
        return sum + Number(item.opportunity_value || 0);
      }, 0));
      setTopTenCurrentRenewal(response.data.top_ten_new_seats_current_renewal.reduce((sum, item) => {
        return sum + Number(item.opportunity_value || 0);
      }, 0));
      setTopTenNext(response.data.top_ten_new_seats_next.reduce((sum, item) => {
        return sum + Number(item.opportunity_value || 0);
      }, 0));
      setTopTenNextRenewal(response.data.top_ten_new_seats_next_renewal.reduce((sum, item) => {
        return sum + Number(item.opportunity_value || 0);
      }, 0));
      setTopTenAfterNext(response.data.top_ten_new_seats_after_next.reduce((sum, item) => {
        return sum + Number(item.opportunity_value || 0);
      }, 0));
      setTopTenAfterNextRenewal(response.data.top_ten_new_seats_after_next_renewal.reduce((sum, item) => {
        return sum + Number(item.opportunity_value || 0);
      }, 0));
    });
  };

  useEffect(() => {
    fetchMetrics();
  }, []);


  const handleMetricClick = (metricType) => {
    let filtered = [];

    switch (metricType) {
      case 'topTenCurrent':
        filtered = metricsData.top_ten_new_seats_current;
        break;
      case 'topTenNext':
        filtered = metricsData.top_ten_new_seats_next;
        break;
      case 'topTenAfterNext':
        filtered = metricsData.top_ten_new_seats_after_next;
        break;
      case 'topTenCurrentRenewal':
        filtered = metricsData.top_ten_new_seats_current_renewal;
        break;
      case 'topTenNextRenewal':
        filtered = metricsData.top_ten_new_seats_next_renewal;
        break;
      case 'topTenAfterNextRenewal':
        filtered = metricsData.top_ten_new_seats_after_next_renewal;
        break;
      default:
        filtered = [];
    }

    setMetricOpportunities(filtered);
    setSelectedMetric(metricType);
    setIsMetricDialogOpen(true);
  };

  return (
    <>
      {/* Key Metrics Carousel */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Tile 1: Value of Top 10 New Seats This Month */}
        <Card className="glass-card h-full">
          <CardContent
            className="p-6 cursor-pointer hover:bg-worldsview-blue/5 transition-colors"
            onClick={() => handleMetricClick('topTenCurrent')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top 10 New Seats </p>
                <p className="text-2xl font-semibold mt-1">{formatZARCurrency(topTenCurrent)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Award className="w-4 h-4 text-worldsview-blue" />
                  <span className="text-sm text-worldsview-blue">{currentMonthName}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-worldsview-blue/20 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-worldsview-blue" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tile 2: Value of Top 10 New Seats Next Month */}
        <Card className="glass-card h-full">
          <CardContent
            className="p-6 cursor-pointer hover:bg-worldsview-purple/5 transition-colors"
            onClick={() => handleMetricClick('topTenNext')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top 10 New Seats </p>
                <p className="text-2xl font-semibold mt-1">{formatZARCurrency(topTenNext)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Award className="w-4 h-4 text-worldsview-purple" />
                  <span className="text-sm text-worldsview-purple">{nextMonthName}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-worldsview-purple/20 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-worldsview-purple" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tile 3: Value of Top 10 New Seats 3 Months */}
        <Card className="glass-card h-full">
          <CardContent
            className="p-6 cursor-pointer hover:bg-worldsview-red/5 transition-colors"
            onClick={() => handleMetricClick('topTenAfterNext')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top 10 New Seats </p>
                <p className="text-2xl font-semibold mt-1">{formatZARCurrency(topTenAfterNext)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Award className="w-4 h-4 text-worldsview-red" />
                  <span className="text-sm text-worldsview-red">{threeMonthRange}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-worldsview-red/20 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-worldsview-red" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tile 4: Value of Top 10 Renewals This Month */}
        <Card className="glass-card h-full">
          <CardContent
            className="p-6 cursor-pointer hover:bg-worldsview-green/5 transition-colors"
            onClick={() => handleMetricClick('topTenCurrentRenewal')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top 10 Renewals </p>
                <p className="text-2xl font-semibold mt-1">{formatZARCurrency(topTenCurrentRenewal)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Repeat className="w-4 h-4 text-worldsview-green" />
                  <span className="text-sm text-worldsview-green">{currentMonthName}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-worldsview-green/20 rounded-full flex items-center justify-center">
                <Repeat className="w-6 h-6 text-worldsview-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tile 5: Value of Top 10 Renewals Next Month */}
        <Card className="glass-card h-full">
          <CardContent
            className="p-6 cursor-pointer hover:bg-worldsview-orange/5 transition-colors"
            onClick={() => handleMetricClick('topTenNextRenewal')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top 10 Renewals </p>
                <p className="text-2xl font-semibold mt-1">{formatZARCurrency(topTenNextRenewal)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Repeat className="w-4 h-4 text-worldsview-orange" />
                  <span className="text-sm text-worldsview-orange">{nextMonthName}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-worldsview-orange/20 rounded-full flex items-center justify-center">
                <Repeat className="w-6 h-6 text-worldsview-orange" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tile 6: Value of Top 10 Renewals 3 Months */}
        <Card className="glass-card h-full">
          <CardContent
            className="p-6 cursor-pointer hover:bg-worldsview-blue/5 transition-colors"
            onClick={() => handleMetricClick('topTenAfterNextRenewal')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top 10 Renewals </p>
                <p className="text-2xl font-semibold mt-1">{formatZARCurrency(topTenAfterNextRenewal)}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Repeat className="w-4 h-4 text-worldsview-blue" />
                  <span className="text-sm text-worldsview-blue">{threeMonthRange}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-worldsview-blue/20 rounded-full flex items-center justify-center">
                <Repeat className="w-6 h-6 text-worldsview-blue" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isMetricDialogOpen} onOpenChange={setIsMetricDialogOpen}>
        <DialogContent className="glass-modal max-w-[calc(100%-2rem)] sm:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-worldsview-blue" />
              {/* {selectedMetric} */}
              Top 10
            </DialogTitle>
            <DialogDescription>
              Viewing {metricOpportunities.length} {metricOpportunities.length === 1 ? 'opportunity' : 'opportunities'} that make up this metric.
            </DialogDescription>
          </DialogHeader>

          {metricOpportunities.length > 0 ? (
            <div className="space-y-4">
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quote</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Close Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metricOpportunities.map((opp) => (
                      <TableRow key={opp.id} className="hover:bg-muted/50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{opp.name}</p>
                            <p className="text-sm text-muted-foreground">{opp.sales_quotation_number}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{opp.end_user?.name}</p>                            
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{formatZARCurrency(opp.opportunity_value)}</p>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`glass-badge font-medium ${opp.process_stage_name === 'Won'
                                ? 'bg-worldsview-green/10 text-worldsview-green border-worldsview-green/20'
                                : opp.stage === 'Negotiation'
                                  ? 'bg-worldsview-orange/10 text-worldsview-orange border-worldsview-orange/20'
                                  : opp.stage === 'Proposal'
                                    ? 'bg-worldsview-blue/10 text-worldsview-blue border-worldsview-blue/20'
                                    : opp.stage === 'Qualification'
                                      ? 'bg-worldsview-purple/10 text-worldsview-purple border-worldsview-purple/20'
                                      : 'bg-muted/50 text-muted-foreground'
                              }`}
                          >
                            {opp.process_stage_name}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                            <span>{opp.expiry_date}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setViewOpportunity(opp);
                              setIsMetricDialogOpen(false);
                              setIsViewOpportunityOpen(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-sm text-muted-foreground">Total Opportunities</Label>
                  <p className="text-2xl font-semibold mt-1">{metricOpportunities.length}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Total Value</Label>
                  <p className="text-2xl font-semibold mt-1">
                    {formatZARCurrency(metricOpportunities.reduce((sum, opp) => {
                      const value = parseFloat(opp.opportunity_value);
                      return sum + (isNaN(value) ? 0 : value);
                    }, 0))}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Average Deal Size</Label>
                  <p className="text-2xl font-semibold mt-1">
                    {formatZARCurrency(metricOpportunities.length > 0 ? (metricOpportunities.reduce((sum, opp) => {
                      const value = parseFloat(opp.opportunity_value);
                      return sum + (isNaN(value) ? 0 : value);
                    }, 0) / metricOpportunities.length) : '0')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-medium mb-2">No Opportunities Found</p>
              <p className="text-sm text-muted-foreground max-w-md">
                There are currently no opportunities in this category. Try selecting a different metric or create a new opportunity.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => setIsMetricDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
